import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { formatTimestampShort } from '@chainflip/utils/date';
import { formatUsdValue } from '@chainflip/utils/number';
import { abbreviate } from '@chainflip/utils/string';
import { tickToRate } from '@chainflip/utils/tickMath';
import classNames from 'classnames';
import { Link, TokenWithChainByAsset } from '@/shared/components';
import { chainflipAssetMap, TokenAmount } from '@/shared/utils';
import { getPercentageFilled, LimitOrderStatus } from './OrderStatus';
import { BuyPill, OrderPrice, OrderTypePill, SellPill } from './RangeOrderRow';

type LimitOrderRowProps = {
  valueUsd?: string | null;
  baseAsset: BaseChainflipAsset;
  lpAccount: string | null;
  creationTimestamp?: string;
  displayCreationTimestamp?: boolean;
  additionalCells?: React.ReactNode;
  onClick?: (asset: BaseChainflipAsset) => void;
  isMobileView?: boolean;
  remainingSellAmount: bigint;
  originalSellAmount: bigint;
  feesEarned: bigint;
  tick: number;
  limitType: 'Sell' | 'Buy';
  isOrderOpen: boolean;
};

type LimitOrderRowMobileProps = Omit<
  LimitOrderRowProps,
  | 'feesEarned'
  | 'limitType'
  | 'valueUsd'
  | 'lpAccount'
  | 'additionalCells'
  | 'onClick'
  | 'isMobileView'
> & {
  isSell: boolean;
  originalSellTokenAmount: TokenAmount;
  buyTokenAmount: TokenAmount;
  baseAssetSymbol: string;
  feeAmount: TokenAmount;
  feeAsset: 'Usdc' | BaseChainflipAsset;
};

type LimitOrderRowDesktopProps = LimitOrderRowMobileProps &
  Pick<LimitOrderRowProps, 'onClick' | 'valueUsd' | 'lpAccount' | 'additionalCells'>;

const LimitOrderRowMobile = ({
  isSell,
  remainingSellAmount,
  originalSellAmount,
  isOrderOpen,
  baseAsset,
  originalSellTokenAmount,
  buyTokenAmount,
  baseAssetSymbol,
  tick,
  feeAmount,
  feeAsset,
  displayCreationTimestamp = true,
  creationTimestamp,
}: LimitOrderRowMobileProps) => {
  const usdcSymbol = chainflipAssetMap.Usdc.symbol;

  return (
    <tr className="flex flex-col justify-center gap-y-2 p-3 font-aeonikMono text-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          {isSell ? <SellPill /> : <BuyPill />}
          <OrderTypePill type="LIMIT" />
        </div>
        <LimitOrderStatus
          fillPercentage={getPercentageFilled(remainingSellAmount, originalSellAmount)}
          isOrderOpen={isOrderOpen}
        />
      </div>
      <div className="flex justify-between gap-x-1 align-top">
        <div className="flex flex-col gap-y-0.5">
          <div className="flex items-center gap-x-2">
            <div className="flex flex-col gap-y-2">
              <span className="flex gap-x-2 text-white">
                <TokenWithChainByAsset asset={baseAsset} />
                <span>
                  {isSell ? originalSellTokenAmount?.toFormatted() : buyTokenAmount?.toFormatted()}
                </span>
                <span>{baseAssetSymbol}</span>
              </span>
            </div>
          </div>
          <span>
            for <OrderPrice baseAsset={baseAsset} tick={tick} /> {usdcSymbol}
          </span>
        </div>
        <div className={classNames(feeAmount.toBigInt() > 0 ? 'text-cf-green-3' : '')}>
          {feeAmount.toBigInt() > 0 && '+'}
          {feeAmount.toFormatted()} {chainflipAssetMap[feeAsset].symbol}
        </div>
      </div>
      {displayCreationTimestamp && (
        <span>
          {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
        </span>
      )}
    </tr>
  );
};

const LimitOrderRowDesktop = ({
  onClick,
  baseAsset,
  isSell,
  originalSellTokenAmount,
  buyTokenAmount,
  baseAssetSymbol,
  valueUsd,
  tick,
  feeAmount,
  feeAsset,
  lpAccount,
  remainingSellAmount,
  originalSellAmount,
  isOrderOpen,
  displayCreationTimestamp = true,
  creationTimestamp,
  additionalCells,
}: LimitOrderRowDesktopProps) => {
  const usdcSymbol = chainflipAssetMap.Usdc.symbol;

  return (
    <tr
      className={classNames(
        onClick && 'transition:ease-in duration-150 hover:cursor-pointer hover:bg-cf-gray-3',
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(baseAsset);
      }}
    >
      <td className="whitespace-nowrap text-12">
        <div className="flex flex-col justify-start gap-y-2 whitespace-nowrap">
          {isSell ? <SellPill /> : <BuyPill />}
          <OrderTypePill type="LIMIT" />
        </div>
      </td>
      <td className="whitespace-nowrap text-12">
        <div className="flex items-center gap-x-2 font-aeonikMono text-cf-light-3">
          <div className="flex flex-col gap-y-2">
            <span className="flex gap-x-2">
              <TokenWithChainByAsset asset={baseAsset} />
              <span>
                {isSell ? originalSellTokenAmount?.toFormatted() : buyTokenAmount?.toFormatted()}
              </span>
              <span>{baseAssetSymbol}</span>
            </span>
          </div>
        </div>
      </td>
      {valueUsd && (
        <td className="whitespace-nowrap font-aeonikMono text-12">{formatUsdValue(valueUsd)}</td>
      )}
      <td className="whitespace-nowrap font-aeonikMono text-12">
        <OrderPrice baseAsset={baseAsset} tick={tick} /> {usdcSymbol}
      </td>
      <td
        className={classNames(
          'whitespace-nowrap font-aeonikMono text-12',
          feeAmount.toBigInt() > 0 ? 'text-cf-green-3' : '',
        )}
      >
        {feeAmount.toBigInt() > 0 && '+'}
        {feeAmount.toFormatted()} {chainflipAssetMap[feeAsset].symbol}
      </td>
      {lpAccount && (
        <td className="whitespace-nowrap font-aeonikMono text-12">
          <Link href={`/lps/${lpAccount}`} noPropagate underline>
            <span>{abbreviate(lpAccount)}</span>
          </Link>
        </td>
      )}
      <td className="whitespace-nowrap font-aeonikMono text-12">
        <LimitOrderStatus
          fillPercentage={getPercentageFilled(remainingSellAmount, originalSellAmount)}
          isOrderOpen={isOrderOpen}
        />
      </td>
      {displayCreationTimestamp && (
        <td className="whitespace-nowrap font-aeonikMono text-12">
          {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
        </td>
      )}
      {additionalCells}
    </tr>
  );
};

export const LimitOrderRow = (props: LimitOrderRowProps) => {
  const isSell = props.limitType === 'Sell';
  const rate = tickToRate(props.tick, props.baseAsset);
  const sellAsset = isSell ? props.baseAsset : 'Usdc';

  const originalSellTokenAmount = TokenAmount.fromAsset(props.originalSellAmount, sellAsset);
  const buyTokenAmount = originalSellTokenAmount.mul(isSell ? rate : 1 / rate);

  const feeAsset = isSell ? 'Usdc' : props.baseAsset;
  const feeAmount = TokenAmount.fromAsset(props.feesEarned, feeAsset);

  const baseAssetSymbol = chainflipAssetMap[props.baseAsset].symbol;

  return props.isMobileView ? (
    <LimitOrderRowMobile
      {...props}
      isSell={isSell}
      originalSellTokenAmount={originalSellTokenAmount}
      buyTokenAmount={buyTokenAmount}
      baseAssetSymbol={baseAssetSymbol}
      feeAmount={feeAmount}
      feeAsset={feeAsset}
    />
  ) : (
    <LimitOrderRowDesktop
      {...props}
      isSell={isSell}
      originalSellTokenAmount={originalSellTokenAmount}
      buyTokenAmount={buyTokenAmount}
      baseAssetSymbol={baseAssetSymbol}
      feeAmount={feeAmount}
      feeAsset={feeAsset}
    />
  );
};
